import React from "react";
import {
  Card,
  CardBody,
  Container,
  Spinner,
  Alert,
} from "reactstrap";
import CardsHeader from "components/Headers/CardsHeader";
import hucsIcon from "assets/img/theme/hucs-icon.png";
import hrmsIcon from "assets/img/theme/hrms-icon.png";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../hucs/util/helpers";
import { serverHost } from "variables/serverdata";
import { triggerDeviceStatusCheck, getSite } from "../hrms/devices/deviceService";

function Dashboard() {
  const navigate = useNavigate();

  // ---------------------------
  // STATE
  // ---------------------------
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [context, setContext] = React.useState(null);
  const [modules, setModules] = React.useState([]);
  const [capacity, setCapacity] = React.useState(null);

  const token = getAuthToken();

  const [selectedSite, setSelectedSite] = React.useState(() => {
    const selectedSiteRaw = localStorage.getItem("selectedSite");
    return selectedSiteRaw ? JSON.parse(selectedSiteRaw) : null;
  });

  const user = JSON.parse(localStorage.getItem("user"));
  const modulePerms = user?.module_perms;

  // ---------------------------
  // LOAD DASHBOARD DATA
  // ---------------------------
  React.useEffect(() => {
    const load = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Context
        const contextRes = await fetch(`${serverHost}/dashboard/context`, {
          headers,
        });
        if (!contextRes.ok) throw new Error("Context failed");
        setContext(await contextRes.json());

        // Modules
        const modulesRes = await fetch(`${serverHost}/dashboard/modules`, {
          headers,
        });
        if (modulesRes.ok) {
          setModules((await modulesRes.json()).modules || []);
        }

        // System Capacity (disk / memory)
        if (selectedSite?.domain_uuid) {
          const sysRes = await fetch(
            `${serverHost}/sys_status/${selectedSite.domain_uuid}`,
            { headers },
          );

          if (sysRes.ok) {
            const data = await sysRes.json();
            const info = data.result || [];

            const getValue = (label) => {
              const item = info.find((i) => i.key === label);
              return item ? parseFloat(item.value) : null;
            };

            const diskTotal = getValue("Disk Total");
            const diskFree = getValue("Disk Free");
            const memTotal = getValue("Memory Total");
            const memFree = getValue("Memory Free");

            setCapacity({
              diskUsedPct:
                diskTotal && diskFree
                  ? Math.round(((diskTotal - diskFree) / diskTotal) * 100)
                  : null,
              memUsedPct:
                memTotal && memFree
                  ? Math.round(((memTotal - memFree) / memTotal) * 100)
                  : null,
              diskTotal,
              diskFree,
              memTotal,
              memFree,
            });
          }
        }
      } catch (err) {
        console.error(err);
        setError("Dashboard failed to load");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token, selectedSite?.domain_uuid]);

  React.useEffect(() => {
    const handleSiteChange = (event) => {
      const newSite = event.detail || JSON.parse(localStorage.getItem("selectedSite") || "null");
      setSelectedSite(newSite);
    };

    window.addEventListener("siteChanged", handleSiteChange);
    return () => window.removeEventListener("siteChanged", handleSiteChange);
  }, []);

  React.useEffect(() => {
    const runInitialDeviceStatusCheck = async () => {
      if (!selectedSite?.site_uuid) return;

      const { site_uuid } = getSite();
      if (!site_uuid) return;

      const key = `hrms_last_dashboard_status_check_${site_uuid}`;
      const lastRun = Number(sessionStorage.getItem(key) || 0);
      const now = Date.now();

      // Only run once every 60 seconds per tab/session
      if (now - lastRun < 60_000) return;

      sessionStorage.setItem(key, String(now));

      try {
        await triggerDeviceStatusCheck(site_uuid, getAuthToken());
      } catch (err) {
        console.warn("Initial dashboard device status check failed:", err);
      }
    };

    runInitialDeviceStatusCheck();
  }, [selectedSite?.site_uuid]);

  // ---------------------------
  // RENDER STATES
  // ---------------------------
  if (loading) {
    return (
      <Container fluid className="mt--6">
        <Card>
          <CardBody className="text-center">
            <Spinner /> Loading dashboard…
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="mt--6">
        <Alert color="danger">{error}</Alert>
      </Container>
    );
  }

  // ---------------------------
  // MODULE CONFIG
  // ---------------------------
  const moduleConfig = {
    HUCS: {
      title: "Module",
      cardValue: "HUCS",
      icon: hucsIcon,
      color: "red",
      path: "/admin/hucs/dashboard",
      description: "Extensions & call routing",
    },
    HRMS: {
      title: "Module",
      cardValue: "HRMS",
      icon: hrmsIcon,
      color: "orange",
      path: "/admin/hrms/dashboard",
      description: "Enterphones",
    },
  };

  const getDisplayRole = (role) => {
    if (role === "MGNT") return "MANAGER";
    return role;
  };

  const headerCards = [
    {
      title: "Current Site",
      value: selectedSite?.company_name || "No site",
      icon: "fas fa-map-marker-alt",
      color: "green",
      text: selectedSite?.city || "No site selected",
    },
    {
      title: "Your Role",
      value: getDisplayRole(context?.role) || "—",
      icon: "fas fa-user-shield",
      color: "blue",
      text: "Current access level",
    },
    ...modules
      .filter(
        (m) =>
          !modulePerms || modulePerms.length === 0 || modulePerms.includes(m),
      )
      .map((m) => moduleConfig[m])
      .filter(Boolean)
      .map((cfg) => ({
        title: cfg.title,
        value: cfg.cardValue,
        icon: cfg.icon,
        color: cfg.color,
        text: cfg.description,
        onClick: () => navigate(cfg.path),
      })),
  ];

  // ---------------------------
  // DASHBOARD UI
  // ---------------------------
  return (
    <>
      <CardsHeader
        name="Dashboard"
        parentName="Home"
        cards={
          (!user?.module_perms || user.module_perms.length === 0) &&
          user?.user_role !== "GLOBAL"
            ? "no_access"
            : headerCards
        }
      />
    </>
  );
}

export default Dashboard;
